import { VariableType } from 'json-to-graphql-query';

import { AttachmentType } from '@/enums/attachmentType.enum';
import { Attachment } from '@/interfaces/attachment';
import { View } from '@/interfaces/view';
import { EntityResource } from './entity.resource';
import { AttachmentFilterVariables } from './queryVariables/filters/filters';

/**
 * Resource załącznika
 *
 * @export
 * @class AttachmentResource
 * @extends {EntityResource<Attachment>}
 */
export class AttachmentResource extends EntityResource<Attachment> {
  public getFields = {
    id: true,
    description: true,
    entityId: true,
    entityType: true,
    metadata: true,
    mediaId: true,
    name: true,
    order: true,
    sent: true,
    type: true,
    viewId: true
  };

  public listFields = {
    id: true,
    description: true,
    entityId: true,
    entityType: true,
    metadata: true,
    mediaId: true,
    name: true,
    order: true,
    sent: true,
    type: true,
    viewId: true
  };

  protected listVariables = AttachmentFilterVariables;

  constructor() {
    super({
      entityName: 'attachment'
    });
  }

  /**
   * Aktualizowanie htmla załącznika
   *
   * @param {*} variables
   * @returns {Promise<number>}
   * @memberof AttachmentResource
   */
  public updateEditedHtmlAttachment(variables: any): Promise<number> {
    const mutation = this.prepareOptions({
      mutation: {
        __variables: {
          html: 'String',
          htmls: '[String!]',
          title: 'String!',
          attachmentId: 'Int!'
        },
        attachmentUpdateEditedHtml: {
          __args: {
            html: new VariableType('html'),
            htmls: new VariableType('htmls'),
            title: new VariableType('title'),
            attachmentId: new VariableType('attachmentId')
          }
        }
      }
    });
    return this.mutation({ mutation, variables });
  }

  /**
   * Generuje ponownie załącznik
   *
   * @param {{ id: number }} variables
   * @returns {Promise<boolean>}
   * @memberof AttachmentResource
   */
  public generateAgain(variables: { id: number }): Promise<boolean> {
    const mutation = this.prepareOptions({
      mutation: { __variables: { id: 'Int!' }, attachmentGenerateAgain: { __args: { id: new VariableType('id') } } }
    });
    return this.mutation({ mutation, variables }, 'attachmentGenerateAgain');
  }

  /**
   * Oznacza załącznik jako wysłany
   *
   * @param {{ id: number }} variables
   * @returns {Promise<boolean>}
   * @memberof AttachmentResource
   */
  public markAsSent(variables: { id: number }): Promise<boolean> {
    const mutation = this.prepareOptions({
      mutation: { __variables: { id: 'Int!' }, attachmentMarkAsSent: { __args: { id: new VariableType('id') } } }
    });
    return this.mutation({ mutation, variables }, 'attachmentMarkAsSent');
  }

  /**
   * Zdejmuje oznaczenie załącznika jako już wysłany
   *
   * @param {{ id: number }} variables
   * @returns {Promise<boolean>}
   * @memberof AttachmentResource
   */
  public reverseMarkAsSent(variables: { id: number }): Promise<boolean> {
    const mutation = this.prepareOptions({
      mutation: { __variables: { id: 'Int!' }, attachmentReverseMarkAsSent: { __args: { id: new VariableType('id') } } }
    });
    return this.mutation({ mutation, variables }, 'attachmentReverseMarkAsSent');
  }

  /**
   * Dodaje załącznik na podstawie UID z mdok
   *
   * @param {{
   *     uid: number;
   *     entityId: string;
   *     entityType: string;
   *     attachmentType: AttachmentType;
   *     additionalMetadata?: any;
   *     documentValues?: any;
   *   }} variables
   * @returns {Promise<number>}
   * @memberof AttachmentResource
   */
  public attachmentAddFromMdok(variables: {
    uid: number;
    entityId: string;
    entityType: string;
    attachmentType: AttachmentType;
    additionalMetadata?: any;
    documentValues?: any;
  }): Promise<Attachment> {
    const mutation = this.prepareOptions({
      mutation: {
        __variables: {
          attachmentType: 'AttachmentType!',
          entityType: 'String!',
          entityId: 'String!',
          uid: 'Int!',
          additionalMetadata: 'JSON',
          documentValues: 'JSON'
        },
        attachmentAddFromMdok: {
          __args: {
            attachmentType: new VariableType('attachmentType'),
            entityType: new VariableType('entityType'),
            entityId: new VariableType('entityId'),
            uid: new VariableType('uid'),
            additionalMetadata: new VariableType('additionalMetadata'),
            documentValues: new VariableType('documentValues')
          },
          ...this.getFields
        }
      }
    });
    return this.mutation({ mutation, variables }, 'attachmentAddFromMdok');
  }

  /**
   * Pobiera listę widoków, które można pobrać grupowo
   *
   * @param {{ filters: any }} variables
   * @returns {Promise<View[]>}
   * @memberof AttachmentResource
   */
  public viewsAvailableToDownload(variables: any): Promise<View[]> {
    const query = this.prepareOptions({
      query: {
        __variables: { documentIds: '[Int!]!' },
        attachmentViewsAvailableToDownload: {
          __args: { documentIds: new VariableType('documentIds') },
          id: true,
          name: true
        }
      }
    });
    return this.query({ query, variables }, 'attachmentViewsAvailableToDownload');
  }
}

export const attachmentResource = new AttachmentResource();
